import { DateTime } from "luxon";

import useAccountWasteReceipts from "../../data/useAccountWasteReceipts";
import { IAccount } from "../../models/account/IAccount";
import formatDateTime from "../../utils/formatDateTime";
import openInNewTab from "../../utils/openInNewTab";
import ClickToCopy from "../shared/ClickToCopy";
import LoadingWheel from "../shared/LoadingWheel";
import WasteReceiptStatusPill from "../wasteTracking/WasteReceiptStatusPill";

// Waste Movements tab on a haulier account: every DEFRA receipt that account delivered.
export default function AccountWasteReceiptList({ account }: { account: IAccount }) {
  const { accountWasteReceipts } = useAccountWasteReceipts(account.id, "AccountWasteReceiptList");

  if (accountWasteReceipts.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <LoadingWheel />
      </div>
    );
  }

  if (accountWasteReceipts.isError || !accountWasteReceipts.data?.data) {
    return (
      <div className="flex items-center justify-center py-10">
        <p className="text-xl font-bold text-red-600">Error loading waste movements</p>
      </div>
    );
  }

  const rows = accountWasteReceipts.data.data;

  if (rows.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-gray-500">
        No waste movements have been recorded against this haulier yet. Pick them as the haulier on a Waste Tracking row
        and it will appear here.
      </p>
    );
  }

  return (
    <div className="overflow-auto">
      <p className="p-2 text-center text-xs text-gray-500 sm:hidden">Tap a row to open the lead</p>
      <table className="min-w-full divide-y divide-gray-300">
        <thead>
          <tr className="bg-primary-50 text-gray-800">
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">Vehicle</th>
            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold sm:table-cell">Collected</th>
            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold sm:table-cell">Received</th>
            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold sm:table-cell">Weight</th>
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">Status</th>
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">Waste tracking ID</th>
            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold sm:table-cell">Submitted</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((row) => (
            <tr
              key={row.vehicleId}
              className={row.leadId ? "cursor-pointer hover:bg-gray-50" : ""}
              onClick={() => row.leadId && openInNewTab(`leads/${row.leadId}`)}
            >
              <td className="px-3 py-3 text-sm">
                <p className="font-medium text-gray-900">{row.vrm ?? "Unknown VRM"}</p>
                <p className="text-xs text-gray-500">{[row.make, row.model].filter(Boolean).join(" ")}</p>
                {row.stockNumber && <p className="text-xs text-gray-500">Stock {row.stockNumber}</p>}
              </td>
              <td className="hidden px-3 py-3 text-sm text-gray-700 sm:table-cell">
                {formatDateTime(row.collectedOn, DateTime.DATETIME_SHORT, "-")}
              </td>
              <td className="hidden px-3 py-3 text-sm text-gray-700 sm:table-cell">
                {formatDateTime(row.dateTimeReceived, DateTime.DATETIME_SHORT, "-")}
              </td>
              <td className="hidden px-3 py-3 text-sm text-gray-700 sm:table-cell">
                {row.weightKg === null ? "-" : `${row.weightKg} kg${row.weightIsEstimate ? " (est.)" : ""}`}
              </td>
              <td className="px-3 py-3 text-sm">
                <WasteReceiptStatusPill row={row} />
              </td>
              <td className="px-3 py-3 text-sm">
                {row.wasteTrackingId ? <ClickToCopy text={row.wasteTrackingId} /> : <span className="text-gray-400">-</span>}
              </td>
              <td className="hidden px-3 py-3 text-sm text-gray-700 sm:table-cell">
                {formatDateTime(row.submittedOn, DateTime.DATETIME_SHORT, "-")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
